<template>
  <SessionModal :open="open" @close="$emit('close')" title="" size="md">
    <div v-if="tutor" class="tutor-profile-modal">
      <!-- Loading State -->
      <div v-if="isLoadingUserData" class="loading-state">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading tutor profile...</p>
      </div>

      <!-- Profile Content -->
      <div
        v-else
        :key="`tutor-${tutor?.id}-${realUserData?.id || 'no-data'}`"
        class="profile-content"
      >
        <!-- Hero Section with Photo and Name -->
        <div class="profile-hero">
          <div class="hero-content">
            <UserAvatar
              :src="tutor?.profileImage"
              :name="tutor?.name || 'Tutor'"
              size="xl"
              class="profile-avatar"
            />
            <div class="hero-info">
              <h2 class="tutor-name">{{ tutor.name }}</h2>
              <div class="rating-badge" :class="{ 'no-rating': isNoRating }">
                <StarIcon class="rating-icon" />
                <span class="rating-text">{{ tutorRating }}</span>
                <span v-if="!isNoRating && getSessionsText()" class="sessions-text">{{
                  getSessionsText()
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- About Section -->
        <div class="about-section">
          <h3 class="section-title">
            <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            About
          </h3>
          <p class="about-text">{{ tutorBio }}</p>
        </div>

        <!-- Subjects Section -->
        <div class="info-section">
          <h3 class="section-title">
            <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            Subjects
          </h3>
          <div class="subject-tags">
            <span v-for="subject in tutor.subjects" :key="subject" class="subject-tag">
              {{ subject }}
            </span>
          </div>
        </div>

        <!-- Info Grid - Education, Experience, Languages -->
        <div class="info-grid">
          <div class="info-card">
            <div class="info-card-header">
              <svg class="info-card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
                />
              </svg>
              <h4 class="info-card-title">Education</h4>
            </div>
            <p class="info-card-text">{{ tutorEducation }}</p>
          </div>

          <div class="info-card">
            <div class="info-card-header">
              <svg class="info-card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <h4 class="info-card-title">Experience</h4>
            </div>
            <p class="info-card-text">{{ tutorExperience }}</p>
          </div>

          <div class="info-card">
            <div class="info-card-header">
              <svg class="info-card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                />
              </svg>
              <h4 class="info-card-title">Languages</h4>
            </div>
            <p class="info-card-text">{{ tutorLanguages }}</p>
          </div>
        </div>

        <!-- Action Button -->
        <div class="action-container">
          <button
            @click="handleBookSession"
            class="book-session-button"
            :class="{ disabled: hasBlockingPendingPostTest }"
            :disabled="hasBlockingPendingPostTest"
          >
            <CalendarIcon class="button-icon" />
            <span>Schedule an Appointment with {{ tutor?.name || "this tutor" }}</span>
          </button>
        </div>
      </div>
    </div>
  </SessionModal>
</template>

<script setup>
import { useRouter } from "vue-router";
import { ref, computed, watch } from "vue";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { logDebug, logInfo, logError } from "@/utils/secureLogger";
import SessionModal from "../Student-Tutor/SessionModal.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import { StarIcon, CalendarIcon } from "@heroicons/vue/24/outline";

const router = useRouter();

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  tutor: {
    type: Object,
    default: null,
  },
  hasBlockingPendingPostTest: {
    type: Boolean,
    default: false,
  },
  pendingPostTest: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["close"]);

// Reactive data for real user data
const realUserData = ref(null);
const isLoadingUserData = ref(false);

// Fetch real user data from Firestore
const fetchRealUserData = async (userId) => {
  if (!userId) return;

  isLoadingUserData.value = true;
  try {
    const userDoc = await getDoc(doc(db, "users", userId));

    if (userDoc.exists()) {
      const userData = userDoc.data();
      realUserData.value = { id: userDoc.id, ...userData };
    } else {
      logError("User document not found for ID:", userId);
      realUserData.value = null;
    }
  } catch (error) {
    logError("Error fetching real user data:", error);
    realUserData.value = null;
  } finally {
    isLoadingUserData.value = false;
  }
};

// Watch for tutor changes and fetch real data
watch(
  () => props.tutor,
  async (newTutor) => {
    if (newTutor && newTutor.id) {
      await fetchRealUserData(newTutor.id);
    }
  },
  { immediate: true }
);

// Helper methods using real data
const getSessionsText = () => {
  if (realUserData.value?.totalSessions && realUserData.value.totalSessions > 0) {
    return `${realUserData.value.totalSessions} session${
      realUserData.value.totalSessions === 1 ? "" : "s"
    }`;
  }
  return "";
};

const getLanguagesText = () => {
  if (
    realUserData.value?.languages &&
    Array.isArray(realUserData.value.languages) &&
    realUserData.value.languages.length > 0
  ) {
    return realUserData.value.languages.join(", ");
  }
  // Default to English, Filipino as requested
  return "English, Filipino";
};

const getAboutText = () => {
  if (realUserData.value?.bio) {
    return realUserData.value.bio;
  }
  // Fallback text if no bio
  return "Experienced tutor dedicated to helping students succeed.";
};

// Computed properties for real data
const tutorBio = computed(() => {
  // First check if tutor object already has bio data
  if (props.tutor?.description) {
    return props.tutor.description;
  }
  // Then check fetched user data
  if (realUserData.value?.bio) {
    return realUserData.value.bio;
  }
  return "Experienced tutor dedicated to helping students succeed.";
});

const tutorRating = computed(() => {
  // First check tutor object (which comes from formatTutorData in FindTutorsView)
  // This ensures consistency between the card and modal
  if (
    props.tutor?.rating &&
    props.tutor.rating !== "No ratings yet" &&
    props.tutor.rating !== "N/A"
  ) {
    return props.tutor.rating;
  }

  // Then check fetched real user data for averageRating
  if (realUserData.value?.averageRating && realUserData.value.averageRating > 0) {
    return `${realUserData.value.averageRating.toFixed(1)}/5.0`;
  }

  return "No ratings yet";
});

const isNoRating = computed(() => {
  return tutorRating.value === "No ratings yet" || tutorRating.value === "N/A";
});

const tutorExperience = computed(() => {
  // First check if tutor object already has experience data
  if (props.tutor?.experience) {
    return props.tutor.experience;
  }
  // Then check fetched user data
  if (realUserData.value?.experienceYears) {
    const years = realUserData.value.experienceYears;
    if (years === 1) {
      return `${years} year of experience`;
    }
    return `${years} years of experience`;
  }
  return "Professional tutoring experience";
});

const tutorEducation = computed(() => {
  // Prioritize fetched user data over tutor object data
  if (realUserData.value?.fieldOfStudy) {
    return realUserData.value.fieldOfStudy;
  }
  // Fallback to tutor object education data
  if (props.tutor?.education) {
    return props.tutor.education;
  }
  return "Qualified educator";
});

const tutorLanguages = computed(() => {
  // First check if tutor object already has languages data
  if (
    props.tutor?.languages &&
    Array.isArray(props.tutor.languages) &&
    props.tutor.languages.length > 0
  ) {
    return props.tutor.languages.join(", ");
  }
  // Then check fetched user data
  if (
    realUserData.value?.languages &&
    Array.isArray(realUserData.value.languages) &&
    realUserData.value.languages.length > 0
  ) {
    return realUserData.value.languages.join(", ");
  }
  return "English, Filipino";
});

const handleBookSession = async () => {
  // Check for pending post-test blocking
  if (props.hasBlockingPendingPostTest && props.pendingPostTest) {
    const Swal = (await import("sweetalert2")).default;
    await Swal.fire({
      icon: "warning",
      title: "⚠️ Pending Post-Test",
      html: `
        <div class="text-left">
          <p class="mb-3"><strong>You need to complete your post-test before scheduling new appointments.</strong></p>
          <div class="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-3">
            <p class="mb-2"><strong>📚 Subject:</strong> <span class="text-gray-700">${
              props.pendingPostTest.sessionSubject
            }</span></p>
            <p class="mb-2"><strong>👨‍🏫 Tutor:</strong> <span class="text-gray-700">${
              props.pendingPostTest.tutorName
            }</span></p>
            <p><strong>📅 Session Date:</strong> <span class="text-gray-700">${new Date(
              props.pendingPostTest.sessionDate.toDate()
            ).toLocaleDateString()}</span></p>
          </div>
          <p class="text-sm text-gray-600">Complete your post-test to continue scheduling appointments.</p>
        </div>
      `,
      confirmButtonText: "OK, I'll complete it now",
    });
    return;
  }

  // Close the modal first
  emit("close");

  // Navigate to book session page with tutor data
  if (props.tutor?.id) {
    router.push({
      name: "book-session-with-tutor",
      params: { tutorId: props.tutor.id },
    });
  } else {
    // Fallback to general booking page
    router.push({ name: "book-session" });
  }
};
</script>

<style scoped>
/* Modern Tutor Profile Modal */
.tutor-profile-modal {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  max-width: 100%;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding-bottom: 30px;
  overflow: visible;
}

/* Hero Section */
.profile-hero {
  margin-bottom: 12px;
}

.hero-content {
  padding: 20px 20px 0;
  text-align: center;
}

.profile-avatar {
  margin: 0 auto 20px;
  border: none;
  box-shadow: 0 12px 32px rgba(30, 64, 175, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  width: 8em !important;
  height: 8em !important;
}

.profile-avatar :deep(span) {
  font-size: 3.5rem !important;
}

.profile-avatar:hover {
  transform: scale(1.02) translateY(-6px);
  box-shadow: 0 20px 48px rgba(30, 64, 175, 0.25), 0 8px 20px rgba(0, 0, 0, 0.12);
}

.hero-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.tutor-name {
  font-size: 26px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.6px;
  background: linear-gradient(135deg, #0f172a 0%, #1e40af 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Rating Badge */
.rating-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  padding: 12px 20px;
  border-radius: 16px;
  border: none;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.3), inset 0 2px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.rating-badge:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 10px 28px rgba(37, 99, 235, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.6);
}

.rating-badge.no-rating {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  box-shadow: 0 6px 16px rgba(156, 163, 175, 0.25), inset 0 2px 0 rgba(255, 255, 255, 0.5);
}

.rating-badge.no-rating:hover {
  box-shadow: 0 10px 28px rgba(156, 163, 175, 0.35), inset 0 2px 0 rgba(255, 255, 255, 0.5);
}

.rating-icon {
  width: 22px;
  height: 22px;
  color: #2563eb;
  filter: drop-shadow(0 2px 4px rgba(37, 99, 235, 0.4));
  animation: gentle-pulse 3s ease-in-out infinite;
}

@keyframes gentle-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.1);
  }
}

.rating-badge.no-rating .rating-icon {
  color: #9ca3af;
  filter: drop-shadow(0 2px 4px rgba(156, 163, 175, 0.4));
}

.rating-text {
  font-size: 18px;
  font-weight: 700;
  color: #1e3a8a;
  letter-spacing: -0.4px;
}

.rating-badge.no-rating .rating-text {
  color: #6b7280;
}

.sessions-text {
  font-size: 14px;
  color: #64748b;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.rating-badge.no-rating .sessions-text {
  color: #9ca3b8;
}

/* Section Styling */
.info-section {
  margin-bottom: 20px;
  padding-left: 25px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 16px 0;
  letter-spacing: -0.5px;
}

.section-icon {
  width: 24px;
  height: 24px;
  color: #3b82f6;
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
}

/* About Section */
.about-section {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: none;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06), inset 0 2px 0 rgba(255, 255, 255, 0.8);
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
}

.about-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
  border-radius: 20px 20px 0 0;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
}

.about-section:hover {
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1), inset 0 2px 0 rgba(255, 255, 255, 0.8);
  transform: translateY(-4px);
}

.about-text {
  font-size: 15px;
  line-height: 1.8;
  color: #334155;
  margin: 0;
  font-weight: 500;
}

/* Subject Tags */
.subject-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.subject-tag {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border: none;
  padding: 10px 18px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 4px 10px rgba(59, 130, 246, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.6);
  position: relative;
  overflow: hidden;
  letter-spacing: -0.2px;
}

.subject-tag::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
  transition: left 0.5s ease;
}

.subject-tag:hover::before {
  left: 100%;
}

.subject-tag:hover {
  background: linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%);
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

/* Info Cards Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 16px;
}

.info-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: none;
  border-radius: 18px;
  padding: 22px;
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.info-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 2px 6px rgba(59, 130, 246, 0.4);
}

.info-card:hover::before {
  transform: scaleX(1);
}

.info-card:hover {
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transform: translateY(-6px) scale(1.02);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.info-card-icon {
  width: 24px;
  height: 24px;
  color: #3b82f6;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
}

.info-card:hover .info-card-icon {
  transform: rotate(15deg) scale(1.2);
}

.info-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.4px;
}

.info-card-text {
  font-size: 15px;
  color: #475569;
  line-height: 1.7;
  margin: 0;
  font-weight: 500;
}

.info-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Info Sections */
.info-section {
  margin-bottom: 0;
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  color: #4c4c4c;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-value {
  font-size: 14px;
  color: #111111;
  line-height: 1.4;
  margin: 0;
}

/* Subject Tags */
.subject-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.subject-tag {
  background: #ecf7ff;
  color: #193cb8;
  border: 1px solid #193cb8;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
}

/* Action Container */
.action-container {
  margin-top: 12px;
  padding: 0 20px;
}

.book-session-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 18px 32px;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 20px rgba(30, 64, 175, 0.35), inset 0 2px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: visible;
  letter-spacing: -0.4px;
}

.book-session-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.6s ease;
}

.book-session-button:hover::before {
  left: 100%;
}

.book-session-button:hover {
  background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #1d4ed8 100%);
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 12px 32px rgba(30, 64, 175, 0.45), inset 0 2px 0 rgba(255, 255, 255, 0.2);
}

.book-session-button:active {
  transform: translateY(-1px) scale(1);
  box-shadow: 0 6px 16px rgba(30, 64, 175, 0.3), inset 0 2px 0 rgba(255, 255, 255, 0.2);
}

.book-session-button.disabled {
  background: linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%);
  color: #6b7280;
  cursor: not-allowed;
  box-shadow: 0 4px 12px rgba(156, 163, 175, 0.2), inset 0 2px 0 rgba(255, 255, 255, 0.2);
  opacity: 0.65;
}

.book-session-button.disabled:hover {
  background: linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%);
  transform: none;
  box-shadow: 0 4px 12px rgba(156, 163, 175, 0.2), inset 0 2px 0 rgba(255, 255, 255, 0.2);
}

.book-session-button.disabled:hover::before {
  left: -100%;
}

.book-session-button.disabled:hover .button-icon {
  transform: none;
  animation: none;
}

.button-icon {
  width: 22px;
  height: 22px;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 1;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.book-session-button:hover .button-icon {
  transform: scale(1.25) rotate(-12deg);
  animation: button-icon-bounce 0.6s ease;
}

@keyframes button-icon-bounce {
  0%,
  100% {
    transform: scale(1.25) rotate(-12deg);
  }
  50% {
    transform: scale(1.35) rotate(-18deg);
  }
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #dbeafe;
  border-top: 5px solid #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.loading-text {
  color: #64748b;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.2px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-content {
    padding: 24px 24px 0;
  }

  .tutor-name {
    font-size: 28px;
  }

  .rating-badge {
    padding: 10px 16px;
  }

  .rating-icon {
    width: 20px;
    height: 20px;
  }

  .rating-text {
    font-size: 16px;
  }

  .sessions-text {
    font-size: 13px;
  }

  .section-title {
    font-size: 17px;
  }

  .section-icon {
    width: 22px;
    height: 22px;
  }

  .about-section {
    padding: 24px;
  }

  .about-text {
    font-size: 14px;
  }

  .subject-tag {
    padding: 9px 16px;
    font-size: 13px;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .info-card {
    padding: 20px;
  }

  .info-card-icon {
    width: 22px;
    height: 22px;
  }

  .info-card-title {
    font-size: 15px;
  }

  .info-card-text {
    font-size: 14px;
  }

  .book-session-button {
    padding: 16px 28px;
    font-size: 16px;
  }

  .button-icon {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 480px) {
  .hero-content {
    padding: 20px 20px 0;
  }

  .profile-avatar {
    width: 8em !important;
    height: 8em !important;
    margin-bottom: 16px;
  }

  .tutor-name {
    font-size: 24px;
  }

  .rating-badge {
    padding: 9px 14px;
  }

  .rating-icon {
    width: 18px;
    height: 18px;
  }

  .rating-text {
    font-size: 15px;
  }

  .sessions-text {
    font-size: 12px;
  }

  .section-title {
    font-size: 16px;
  }

  .section-icon {
    width: 20px;
    height: 20px;
  }

  .about-section {
    padding: 20px;
  }

  .about-text {
    font-size: 14px;
  }

  .subject-tag {
    padding: 8px 14px;
    font-size: 12px;
  }

  .info-card {
    padding: 18px;
  }

  .info-card-icon {
    width: 20px;
    height: 20px;
  }

  .info-card-title {
    font-size: 14px;
  }

  .info-card-text {
    font-size: 13px;
  }

  .book-session-button {
    padding: 15px 24px;
    font-size: 15px;
  }

  .button-icon {
    width: 19px;
    height: 19px;
  }
}
</style>
